// src/pages/admin/AdminDashboard.jsx - FINAL VERSION (NO PLACEHOLDERS)

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, firebaseApp } from '../../firebase'; 
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import Toast from '../../components/Toast';
import styles from './AdminDashboard.module.css';
import { getTrackableSeries } from '../../data/testSeriesData';

// Initialize functions SDK
const functions = getFunctions(firebaseApp); 

const TRACKABLE_SERIES = getTrackableSeries();


const AdminDashboard = () => {
    const { currentUser, userData } = useAuth();
    // All hooks must be called before any return!
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalRevenue: 0,
        lastTransactions: [],
        activeSubscriptions: 0,
        totalSuperadmins: 0,
        totalAdmins: 0,
        totalNormalUsers: 0,
        adminWithPurchases: 0,
        userWithPurchases: 0,
        superadminWithPurchases: 0
    });
    const [userMap, setUserMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    // Delete Subscription UI state
    const [deleteUserOptions, setDeleteUserOptions] = useState([]); // [{id, name, email, purchasedCourses: []}]
    // No filter/search state needed
    const [selectedDeleteUser, setSelectedDeleteUser] = useState(null); // user object
    const [deletingCourseId, setDeletingCourseId] = useState('');
    // States for Subscription Analytics
    const [selectedCourseId, setSelectedCourseId] = useState(TRACKABLE_SERIES[0]?.id || '');
    const [subscribedUsers, setSubscribedUsers] = useState([]);
    const [subscribedUserMap, setSubscribedUserMap] = useState({});
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [subsSearch, setSubsSearch] = useState('');
    const [subsDateRange, setSubsDateRange] = useState('all');
    const [subsPage, setSubsPage] = useState(1);
    const [subsPerPage, setSubsPerPage] = useState(10);

    const isSuperadmin = userData?.role === 'superadmin';
    const canView = isSuperadmin || userData?.dashboardAccess?.dashboard;

    const showToast = (message, type = "success") => setToast({ message, type });

    // Function to fetch all necessary admin data (Stats & Transactions)
    const fetchDashboardData = useCallback(async () => {
        if (!currentUser) return;

        try {
            // 1. Fetch Total Users and Roles
            const userSnapshot = await getDocs(collection(db, "users"));
            const totalUsers = userSnapshot.size;
            const userInfoMap = {};
            let totalSuperadmins = 0;
            let totalAdmins = 0;
            let totalNormalUsers = 0;
            userSnapshot.forEach(userDoc => {
                const userData = userDoc.data();
                userInfoMap[userDoc.id] = { role: userData.role };
                if (userData.role === 'superadmin') totalSuperadmins++;
                else if (userData.role === 'admin') totalAdmins++;
                else totalNormalUsers++;
            });

            // 2. Fetch ALL successful transactions
            const txAllQuery = query(collection(db, "transactions"), where("status", "==", "success"));
            const txAllSnapshot = await getDocs(txAllQuery);
            // Group by userId
            const userPurchaseSet = new Set();
            const adminPurchaseSet = new Set();
            const superadminPurchaseSet = new Set();
            let totalRevenue = 0;
            txAllSnapshot.forEach(docSnap => {
                const tx = docSnap.data();
                totalRevenue += tx.amount || 0;
                if (tx.userId) {
                    userPurchaseSet.add(tx.userId);
                    if (userInfoMap[tx.userId]?.role === 'admin') adminPurchaseSet.add(tx.userId);
                    if (userInfoMap[tx.userId]?.role === 'superadmin') superadminPurchaseSet.add(tx.userId);
                }
            });
            const userWithPurchases = Array.from(userPurchaseSet).filter(uid => userInfoMap[uid]?.role !== 'admin' && userInfoMap[uid]?.role !== 'superadmin').length;
            const adminWithPurchases = adminPurchaseSet.size;
            const superadminWithPurchases = superadminPurchaseSet.size;
            const activeSubscriptions = userPurchaseSet.size;

            // 3. Fetch Recent Transactions for table
            const txQuery = query(collection(db, "transactions"), orderBy("date", "desc"), limit(5));
            const txSnapshot = await getDocs(txQuery);
            const lastTransactions = txSnapshot.docs.map(doc => {
                const data = doc.data();
                return { id: doc.id, ...data, date: data.date };
            });

            // Fetch user names for all unique userIds in transactions
            const uids = Array.from(new Set(lastTransactions.map(tx => tx.userId).filter(Boolean)));
            const userMapTemp = {};
            await Promise.all(uids.map(async (uid) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        userMapTemp[uid] = data.username || data.displayName || data.name || '';
                    }
                } catch {}
            }));
            setUserMap(userMapTemp);

            setStats({
                totalUsers,
                totalRevenue: totalRevenue.toFixed(2),
                lastTransactions,
                activeSubscriptions,
                totalSuperadmins,
                totalAdmins,
                totalNormalUsers,
                adminWithPurchases,
                userWithPurchases,
                superadminWithPurchases
            });
        } catch (error) {
            console.error("Error fetching admin data:", error);
            showToast("Error loading dashboard data. Check Console.", "error");
        }
        setLoading(false);
    }, [currentUser]);

    // Function to fetch users subscribed to the selected course
    const fetchSubscribedUsers = useCallback(async () => {
        if (!canView || !selectedCourseId) {
            setSubscribedUsers([]);
            setSubscribedUserMap({});
            return;
        }

        setLoadingSubs(true);
        try {
            // Fetch all successful transactions for this course
            const txQuery = query(
                collection(db, "transactions"),
                where("courseId", "==", selectedCourseId),
                where("status", "==", "success")
            );
            const txSnapshot = await getDocs(txQuery);
            // Group by userId (unique subscribers)
            const userIds = Array.from(new Set(txSnapshot.docs.map(doc => doc.data().userId).filter(Boolean)));
            // Fetch user info for each subscriber
            const usersList = [];
            const userMapTemp = {};
            await Promise.all(userIds.map(async (uid) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        userMapTemp[uid] = data.username || data.displayName || data.name || '';
                        // Find the earliest purchase date for this user/course
                        const userTxs = txSnapshot.docs.filter(doc => doc.data().userId === uid);
                        const earliestTx = userTxs.reduce((min, doc) => {
                            const d = doc.data();
                            if (!min) return d;
                            if (d.date && (!min.date || d.date.toMillis() < min.date.toMillis())) return d;
                            return min;
                        }, null);
                        const rawDate = earliestTx?.date?.toDate ? earliestTx.date.toDate() : null;
                        usersList.push({
                            id: uid,
                            email: data.email || "N/A",
                            purchaseDateRaw: rawDate,
                            purchaseDate: rawDate ? rawDate.toLocaleDateString() : "N/A"
                        });
                    }
                } catch {}
            }));
            setSubscribedUserMap(userMapTemp);
            setSubscribedUsers(usersList);
        } catch (error) {
            console.error("❌ Error fetching subscribed users:", error);
            showToast(`Error fetching subscriptions: ${error.message}`, "error");
            setSubscribedUsers([]);
            setSubscribedUserMap({});
        }
        setLoadingSubs(false);
    }, [canView, selectedCourseId]);

    // Delete a specific course for a user
    const handleDeleteUserCourse = async (user, courseId) => {
        if (!user || !courseId) return;
        if (!window.confirm(`Are you sure you want to delete subscription '${courseId}' for user ${user.name || user.email || user.id}? THIS CANNOT BE UNDONE.`)) {
            return;
        }
        setDeletingCourseId(courseId);
        showToast("Attempting to delete subscription...", "info");
        try {
            const deleteSub = httpsCallable(functions, 'deleteUserSubscription');
            const result = await deleteSub({ userId: user.id, subscriptionId: courseId });
            showToast(result.data.message || 'Subscription deleted', "success");
            // Remove course from UI instantly
            setDeleteUserOptions(prev => prev.map(u => u.id === user.id ? { ...u, purchasedCourses: u.purchasedCourses.filter(cid => cid !== courseId) } : u));
            setSelectedDeleteUser(sel => sel && sel.id === user.id ? { ...sel, purchasedCourses: sel.purchasedCourses.filter(cid => cid !== courseId) } : sel);
        } catch (error) {
            console.error("Client error deleting subscription:", error);
            const msg = error.details?.message || error.message;
            showToast(`Deletion failed: ${msg}`, "error");
        }
        setDeletingCourseId('');
    };

    // Fetch all paid users and their purchasedCourses for deletion UI
    const fetchDeleteUserOptions = useCallback(async () => {
        try {
            const txQuery = query(collection(db, "transactions"), where("status", "==", "success"));
            const txSnapshot = await getDocs(txQuery);
            const userCourseMap = {};
            txSnapshot.forEach(docSnap => {
                const tx = docSnap.data();
                if (tx.userId && tx.courseId) {
                    if (!userCourseMap[tx.userId]) userCourseMap[tx.userId] = new Set();
                    userCourseMap[tx.userId].add(tx.courseId);
                }
            });
            const userIds = Object.keys(userCourseMap);
            const userList = [];
            await Promise.all(userIds.map(async (uid) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        userList.push({
                            id: uid,
                            name: data.username || data.displayName || data.name || '',
                            email: data.email || '',
                            purchasedCourses: Array.from(userCourseMap[uid])
                        });
                    }
                } catch {}
            }));
            setDeleteUserOptions(userList);
        } catch (error) {
            setDeleteUserOptions([]);
        }
    }, [db]);

    useEffect(() => {
        if (isSuperadmin) fetchDeleteUserOptions();
    }, [isSuperadmin, fetchDeleteUserOptions]);

    useEffect(() => {
        setLoading(true);
        if (!currentUser || !canView) {
            setLoading(false);
            return;
        }
        fetchDashboardData();
    }, [currentUser, canView, fetchDashboardData]);

    useEffect(() => {
        if (canView && selectedCourseId) {
            fetchSubscribedUsers();
        }
    }, [canView, selectedCourseId, fetchSubscribedUsers]);

    // Reset subscription pagination when course, search, date or per-page changes
    useEffect(() => { setSubsPage(1); }, [selectedCourseId, subsSearch, subsDateRange, subsPerPage]);

    if (!canView) {
        return <p className={styles.accessDenied}>🚨 Access Denied. You do not have permission to view this page.</p>;
    }
    if (loading) return <p className="loading-text">Loading Admin Panel...</p>;

    const selectedCourseTitle = TRACKABLE_SERIES.find(s => s.id === selectedCourseId)?.title || selectedCourseId;

    // Subscription analytics: filter by date range + search
    const getSubsDateLimit = () => {
        if (subsDateRange === 'all') return null;
        const months = { '1m': 1, '3m': 3, '6m': 6, '1y': 12, '2y': 24 };
        const d = new Date();
        d.setMonth(d.getMonth() - (months[subsDateRange] || 1));
        return d;
    };
    const filteredSubs = subscribedUsers.filter(user => {
        const dateLimit = getSubsDateLimit();
        if (dateLimit && (!user.purchaseDateRaw || user.purchaseDateRaw < dateLimit)) return false;
        if (!subsSearch) return true;
        const name = (subscribedUserMap[user.id] || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const term = subsSearch.toLowerCase();
        return name.includes(term) || email.includes(term);
    });
    const totalSubsPages = Math.max(1, Math.ceil(filteredSubs.length / subsPerPage));
    const safeSubsPage = Math.min(subsPage, totalSubsPages);
    const paginatedSubs = filteredSubs.slice((safeSubsPage - 1) * subsPerPage, safeSubsPage * subsPerPage);

    return (
        <div className={styles.adminContainer}>
            {/* Premium Admin Header */}
            <div className={styles.adminHeader}>
                <div className={styles.headerContent}>
                    <h1 className={styles.mainTitle}>
                        <span className={styles.titleIcon}>👑</span>
                        Admin Dashboard
                    </h1>
                    <p className={styles.welcomeMsg}>
                        <span className={styles.welcomeIcon}>👋</span>
                        Welcome back, <strong>{currentUser.email}</strong>
                    </p>
                    <p className={styles.subtitle}>Manage users, sales, and subscriptions with powerful analytics</p>
                </div>
            </div>
            
            {/* 1. PREMIUM STATS GRID */}
            <section className={styles.statsSection}>
                <h2 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>📊</span>
                    Platform Statistics
                </h2>
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>👥</div>
                        <div className={styles.statContent}>
                            <h3>Total Registered Users</h3>
                            <p className={styles.statValue}>{stats.totalUsers.toLocaleString()}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>🦸‍♂️</div>
                        <div className={styles.statContent}>
                            <h3>Superadmins</h3>
                            <p className={styles.statValue}>{stats.totalSuperadmins.toLocaleString()}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>🔑</div>
                        <div className={styles.statContent}>
                            <h3>Admins</h3>
                            <p className={styles.statValue}>{stats.totalAdmins.toLocaleString()}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>👤</div>
                        <div className={styles.statContent}>
                            <h3>Students</h3>
                            <p className={styles.statValue}>{stats.totalNormalUsers.toLocaleString()}</p>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>💳</div>
                        <div className={styles.statContent}>
                            <h3>Active Subscriptions</h3>
                            <p className={styles.statValue}>{stats.activeSubscriptions.toLocaleString()}</p>
                            <div className={styles.statBreakdown}>
                                <span className={styles.breakdownItem}>
                                    <span className={styles.breakdownLabel}>Superadmins:</span>
                                    <span className={styles.breakdownValue}>{stats.superadminWithPurchases.toLocaleString()}</span>
                                </span>
                                <span className={styles.breakdownDivider}>|</span>
                                <span className={styles.breakdownItem}>
                                    <span className={styles.breakdownLabel}>Admins:</span>
                                    <span className={styles.breakdownValue}>{stats.adminWithPurchases.toLocaleString()}</span>
                                </span>
                                <span className={styles.breakdownDivider}>|</span>
                                <span className={styles.breakdownItem}>
                                    <span className={styles.breakdownLabel}>Students:</span>
                                    <span className={styles.breakdownValue}>{stats.userWithPurchases.toLocaleString()}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.statCard} ${styles.revenueCard}`}>
                        <div className={styles.statIcon}>💰</div>
                        <div className={styles.statContent}>
                            <h3>Total Revenue</h3>
                            <p className={styles.statValue}>₹{parseFloat(stats.totalRevenue).toLocaleString('en-IN')}</p>
                        </div>
                    </div>
                </div> {/* Close statsGrid */}
            </section>
            
            {/* 2. PREMIUM RECENT TRANSACTIONS */}
            <section className={styles.recentTransactions}>
                <h2 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>📋</span>
                    Recent Transactions
                </h2>
                <div className={styles.tableContainer}>
                    <table className={styles.transactionTable}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>User ID (First 5)</th>
                                <th>Name</th>
                                <th>Course ID</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.lastTransactions.map(tx => (
                                <tr key={tx.id}>
                                    <td>{tx.date?.toDate ? tx.date.toDate().toLocaleDateString() : 'N/A'}</td>
                                    <td>{tx.userId ? tx.userId.substring(0, 5) + '...' : 'N/A'}</td>
                                    <td>{userMap[tx.userId] || 'N/A'}</td>
                                    <td>{tx.courseId}</td>
                                    <td className={styles.amountCell}>₹{tx.amount.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
            
            {/* 3. SUBSCRIPTION ANALYTICS SECTION */}
            <section className={styles.subscriptionAnalytics}>
                <h2 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>📊</span>
                    Student Subscription Analytics
                </h2>
                <div className={styles.filterBar}>
                    <label htmlFor="course-filter">Test Series:</label>
                    <select 
                        id="course-filter"
                        className={styles.selectField}
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                    >
                        {TRACKABLE_SERIES.map(series => (
                            <option key={series.id} value={series.id}>
                                {series.title}
                            </option>
                        ))}
                    </select>
                    <select
                        value={subsDateRange}
                        onChange={e => setSubsDateRange(e.target.value)}
                        className={styles.selectField}
                    >
                        <option value="all">All Time</option>
                        <option value="1m">Last 30 Days</option>
                        <option value="3m">Last 3 Months</option>
                        <option value="6m">Last 6 Months</option>
                        <option value="1y">Last Year</option>
                        <option value="2y">Last 2 Years</option>
                    </select>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={subsSearch}
                        onChange={e => setSubsSearch(e.target.value)}
                        className={styles.subsSearchInput}
                    />
                </div>

                <h3 className={styles.userListTitle}>
                    Subscribers for: <strong>{selectedCourseTitle}</strong>
                    {' '}({filteredSubs.length}{(subsSearch || subsDateRange !== 'all') ? ` of ${subscribedUsers.length}` : ''} total)
                </h3>
                
                <div className={styles.tableContainer}>
                    {loadingSubs ? (
                        <p className={styles.loadingSubs}>Fetching subscriber list...</p>
                    ) : filteredSubs.length === 0 ? (
                        <p className={styles.noData}>{subscribedUsers.length === 0 ? 'No students subscribed to this category found.' : 'No results match your search.'}</p>
                    ) : (
                        <table className={styles.analyticsTable}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Purchase Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedSubs.map((user, index) => (
                                    <tr key={user.id}>
                                        <td>{(safeSubsPage - 1) * subsPerPage + index + 1}</td>
                                        <td>{subscribedUserMap[user.id] || 'N/A'}</td>
                                        <td>{user.email}</td>
                                        <td>{user.purchaseDate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Subscription Pagination Controls */}
                {filteredSubs.length > 0 && !loadingSubs && (
                    <div className={styles.pagination}>
                        <span className={styles.paginationInfo}>
                            Showing {(safeSubsPage - 1) * subsPerPage + 1}–{Math.min(safeSubsPage * subsPerPage, filteredSubs.length)} of {filteredSubs.length} subscribers
                        </span>
                        <div className={styles.paginationControls}>
                            <button className={styles.pageBtn} onClick={() => setSubsPage(1)} disabled={safeSubsPage === 1}>«</button>
                            <button className={styles.pageBtn} onClick={() => setSubsPage(p => p - 1)} disabled={safeSubsPage === 1}>‹ Prev</button>
                            <span className={styles.pageInfo}>Page {safeSubsPage} of {totalSubsPages}</span>
                            <button className={styles.pageBtn} onClick={() => setSubsPage(p => p + 1)} disabled={safeSubsPage >= totalSubsPages}>Next ›</button>
                            <button className={styles.pageBtn} onClick={() => setSubsPage(totalSubsPages)} disabled={safeSubsPage >= totalSubsPages}>»</button>
                            <select
                                value={subsPerPage}
                                onChange={e => { setSubsPerPage(Number(e.target.value)); setSubsPage(1); }}
                                className={styles.perPageSelect}
                            >
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                            </select>
                        </div>
                    </div>
                )}
            </section>
            {isSuperadmin && (
                <section className={styles.managementSection}>
                    <div className={styles.dangerZoneHeader}>
                        <h2 className={styles.sectionTitle}>
                            <span className={styles.sectionIcon}>⚠️</span>
                            Subscription Deletion
                        </h2>
                        <span className={styles.dangerBadge}>DANGER ZONE</span>
                    </div>
                    <p className={styles.warningText}>
                        <span className={styles.warningIcon}>⚠️</span>
                        This action permanently deletes a user's subscription record from Firestore. Use with extreme caution.
                    </p>
                    <div className={styles.managementForm}>
                        <select
                            className={styles.inputField}
                            value={selectedDeleteUser ? selectedDeleteUser.id : ''}
                            onChange={e => {
                                const user = deleteUserOptions.find(u => u.id === e.target.value);
                                setSelectedDeleteUser(user || null);
                            }}
                        >
                            <option value="">Select paid user to delete...</option>
                            {deleteUserOptions.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name || u.email || u.id} ({u.email}) [{u.purchasedCourses.length}]
                                </option>
                            ))}
                        </select>
                        {selectedDeleteUser && (
                            <div className={styles.deleteTableWrap}>
                                <table className={styles.analyticsTable}>
                                    <thead>
                                        <tr>
                                            <th>Course ID</th>
                                            <th>Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedDeleteUser.purchasedCourses.length === 0 ? (
                                            <tr><td colSpan={2}>No active subscriptions.</td></tr>
                                        ) : (
                                            selectedDeleteUser.purchasedCourses.map(cid => (
                                                <tr key={cid}>
                                                    <td>{cid}</td>
                                                    <td>
                                                        <button
                                                            className={styles.deleteBtn}
                                                            disabled={deletingCourseId === cid}
                                                            onClick={() => handleDeleteUserCourse(selectedDeleteUser, cid)}
                                                        >
                                                            {deletingCourseId === cid ? 'Deleting...' : 'Delete'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </section>
            )}


            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default AdminDashboard;